import { Msg, NatsConnection, Subscription } from "@nats-io/nats-core";
import {
    PresenceChannelParams,
    PresenceKind,
    PresenceMessage,
    PresenceStatus,
    getPresenceChannelSubject,
    getPresenceHeartbeatSubject,
    getPresenceProbeTarget,
    getPresenceTtlMs,
    parsePresenceHeartbeatSubject,
    presenceMessageSchema,
} from "@common/types/presence-nats";
import {
    ChannelPresence,
    EMPTY_CHANNEL_PRESENCE,
    NO_USER_IDS,
    applyChannelMessage,
    expireChannel,
    getPresenceTargetKey,
} from "./presence.lib";

const EXPIRY_SWEEP_INTERVAL_MS = 1_000;
/** Lets a table that remounts its rows (paging, regrouping) reuse the channel instead of resubscribing. */
const DISPOSE_GRACE_MS = 5_000;

interface Channel {
    params: PresenceChannelParams;
    presence: ChannelPresence;
    listeners: Set<() => void>;
    connection: NatsConnection | null;
    subscription: Subscription | null;
    selfUserId: number | undefined;
    sweepTimerId: number;
    disposeTimerId: number | null;
}

export interface PresenceSubscription {
    channel: PresenceChannelParams;
    connection: NatsConnection | null;
    selfUserId: number | undefined;
}

const channels = new Map<string, Channel>();

const notify = (channel: Channel) => {
    channel.listeners.forEach((listener) => listener());
};

const readPresenceMessage = (message: Msg): PresenceMessage | null => {
    try {
        const parsed = presenceMessageSchema.safeParse(message.json());
        return parsed.success ? parsed.data : null;
    } catch {
        return null;
    }
};

const handleMessage = (channel: Channel, message: Msg) => {
    const sender = parsePresenceHeartbeatSubject(message.subject);
    const isForeignHeartbeat =
        !!sender &&
        sender.kind === channel.params.kind &&
        sender.userId !== channel.selfUserId;
    const payload = readPresenceMessage(message);
    if (!isForeignHeartbeat || !payload) {
        return;
    }

    const targetKey = getPresenceTargetKey(sender);
    const previous = channel.presence;
    channel.presence = applyChannelMessage({
        channel: previous,
        targetKey,
        userId: sender.userId,
        status: payload.status,
        ttlMs: getPresenceTtlMs(sender.kind),
        now: Date.now(),
    });

    const haveViewersChanged =
        previous.userIdsByTarget.get(targetKey) !==
        channel.presence.userIdsByTarget.get(targetKey);
    if (haveViewersChanged) {
        notify(channel);
    }
};

/** Asks live viewers to heartbeat now so a fresh observer does not wait for the next beat. */
const announceObserver = ({
    params,
    connection,
    selfUserId,
}: Pick<Channel, "params" | "connection" | "selfUserId">) => {
    const canAnnounce =
        !!connection &&
        selfUserId !== undefined &&
        params.kind !== PresenceKind.CHAT;
    if (!canAnnounce) {
        return;
    }

    try {
        connection.publish(
            getPresenceHeartbeatSubject({
                ...getPresenceProbeTarget({
                    scope: params.scope,
                    observedKind: params.kind,
                }),
                userId: selfUserId,
            }),
            JSON.stringify({ status: PresenceStatus.JOINED }),
        );
    } catch {
        // Best-effort: a closed connection is re-established by its provider
    }
};

const closeSubscription = (channel: Channel) => {
    channel.subscription?.unsubscribe();
    channel.subscription = null;
};

const openSubscription = (subject: string, channel: Channel) => {
    if (!channel.connection) {
        return;
    }

    try {
        const subscription = channel.connection.subscribe(subject);
        channel.subscription = subscription;
        void (async () => {
            try {
                for await (const message of subscription) {
                    handleMessage(channel, message);
                }
            } catch {
                if (channel.subscription === subscription) {
                    channel.subscription = null;
                }
            }
        })();
        announceObserver(channel);
    } catch {
        // Best-effort: a closed connection is re-established by its provider
    }
};

const createChannel = (
    subject: string,
    params: PresenceChannelParams,
): Channel => {
    const channel: Channel = {
        params,
        presence: EMPTY_CHANNEL_PRESENCE,
        listeners: new Set(),
        connection: null,
        subscription: null,
        selfUserId: undefined,
        disposeTimerId: null,
        sweepTimerId: window.setInterval(() => {
            const unexpired = expireChannel({
                channel: channel.presence,
                ttlMs: getPresenceTtlMs(params.kind),
                now: Date.now(),
            });
            if (unexpired !== channel.presence) {
                channel.presence = unexpired;
                notify(channel);
            }
        }, EXPIRY_SWEEP_INTERVAL_MS),
    };
    channels.set(subject, channel);
    return channel;
};

const disposeChannel = (subject: string, channel: Channel) => {
    window.clearInterval(channel.sweepTimerId);
    closeSubscription(channel);
    channels.delete(subject);
};

/**
 * Shares one NATS subscription per channel between every component that
 * observes it, so a table of rows costs one subscription, not one per row.
 * Returns the unsubscribe function, shaped for `useSyncExternalStore`.
 */
export const subscribeToPresence = (
    { channel: params, connection, selfUserId }: PresenceSubscription,
    onChange: () => void,
): (() => void) => {
    const subject = getPresenceChannelSubject(params);
    const channel = channels.get(subject) ?? createChannel(subject, params);

    if (channel.disposeTimerId !== null) {
        window.clearTimeout(channel.disposeTimerId);
        channel.disposeTimerId = null;
    }
    channel.listeners.add(onChange);
    channel.selfUserId = selfUserId;

    if (connection && connection !== channel.connection) {
        closeSubscription(channel);
        channel.connection = connection;
        openSubscription(subject, channel);
    }

    return () => {
        channel.listeners.delete(onChange);
        if (channel.listeners.size > 0) {
            return;
        }

        channel.disposeTimerId = window.setTimeout(
            () => disposeChannel(subject, channel),
            DISPOSE_GRACE_MS,
        );
    };
};

export const getPresentUserIdsOfTarget = ({
    channelSubject,
    targetKey,
}: {
    channelSubject: string;
    targetKey: string;
}): number[] =>
    channels.get(channelSubject)?.presence.userIdsByTarget.get(targetKey) ??
    NO_USER_IDS;
